<template>
  <div class="rentalplan-detail">
    <van-nav-bar :fixed="true" z-index="9" :title="title" :border="false" left-arrow @click-left="onClickLeft" />
    <van-pull-refresh
      class="detail-pull-refresh"
      v-model="refreshing"
      :pulling-text="$t('loading')"
      :loosing-text="$t('loading')"
      :loading-text="$t('loading')"
      success-text=""
      :success-duration="0"
      @refresh="onRefresh"
    >
      <div v-if="loading" class="bill-detail-content bill-detail-skeleton" aria-hidden="true">
        <section class="bill-card bill-skeleton-card bill-skeleton-order">
          <div class="bill-skeleton-row">
            <span class="bill-skeleton-line bill-skeleton-line--order"></span>
            <span class="bill-skeleton-pill"></span>
          </div>
          <div class="bill-divider"></div>
          <div class="bill-skeleton-product">
            <span class="bill-skeleton-image"></span>
            <span class="bill-skeleton-copy">
              <i class="bill-skeleton-line bill-skeleton-line--title"></i>
              <i class="bill-skeleton-line bill-skeleton-line--subtitle"></i>
            </span>
          </div>
          <div class="bill-divider"></div>
          <div class="bill-skeleton-grid">
            <span class="bill-skeleton-block"></span>
            <span class="bill-skeleton-block"></span>
          </div>
        </section>
        <section class="bill-card bill-skeleton-card">
          <div class="bill-skeleton-line bill-skeleton-line--section"></div>
          <div class="bill-skeleton-summary">
            <span class="bill-skeleton-line bill-skeleton-line--label"></span>
            <span class="bill-skeleton-line bill-skeleton-line--amount"></span>
            <span class="bill-skeleton-line"></span>
            <span class="bill-skeleton-line"></span>
          </div>
          <span class="bill-skeleton-button"></span>
        </section>
        <section class="bill-card bill-skeleton-action"></section>
        <section class="bill-card bill-skeleton-action"></section>
      </div>
      <div v-else-if="loadError" class="bill-detail-content bill-detail-error">
        <section class="bill-card bill-error-card">
          <span class="bill-error-icon"><van-icon name="warning-o" /></span>
          <div class="bill-error-title">{{ $t('systemError') }}</div>
          <div v-if="loadErrorMessage" class="bill-error-desc">{{ loadErrorMessage }}</div>
          <button type="button" class="bill-error-retry" @click="loadPage">{{ $t('retry') }}</button>
          <button type="button" class="bill-error-back" @click="onClickLeft">{{ $t('back') }}</button>
        </section>
      </div>
      <div v-else-if="!orderDetail || planList.length === 0" class="plan-state">
        {{ $t('rentalplan.empty') }}
      </div>

      <main v-else class="bill-detail-content">
        <section class="bill-order-card bill-card">
        <div class="bill-card__header">
          <div class="bill-order-number text-ellipsis">
            {{ displayOrderNumber }}
            <span class="bill-copy" :aria-label="$t('planDetail.copyOrderNumber')" role="button" @click="copyOrderNumber"></span>
          </div>
          <span class="bill-status" :class="billStatusClass">{{ billStatusText }}</span>
        </div>
        <div class="bill-divider"></div>
        <div class="bill-product-row">
          <div class="bill-product-image" v-lazy:background-image="displayOrderInfo.goodsPic || displayOrderInfo.imageUrl"></div>
          <div class="bill-product-copy">
            <div class="bill-product-name text-ellipsis">{{ displayOrderInfo.goodsName || displayOrderInfo.goodName || $t('rentalplan.unknownProduct') }}</div>
            <div class="bill-product-sku text-ellipsis">{{ displaySkuText }}</div>
          </div>
        </div>
        <div class="bill-divider"></div>
        <div class="bill-time-grid">
          <div class="bill-time-block">
            <span>{{ $t('planDetail.orderTime') }}</span>
            <strong><span class="bill-date-icon"></span><span class="bill-date-value">{{ formatDisplayDateTime(displayOrderInfo.createTime) }}</span></strong>
          </div>
          <div class="bill-time-block">
            <span>{{ $t('planDetail.receivedTime') }}</span>
            <strong><span class="bill-date-icon"></span><span class="bill-date-value">{{ formatDisplayDateTime(displayOrderInfo.receiveTime) }}</span></strong>
          </div>
        </div>
      </section>

      <section class="bill-details-card bill-card">
        <div class="bill-section-title">
          <span class="bill-section-icon"><van-icon name="bill-o" /></span>
          <span>{{ $t('planDetail.billDetails') }}</span>
        </div>
        <div class="bill-summary-card">
          <div class="bill-summary-head">
            <div>
              <span class="bill-label">{{ $t('planDetail.billTotalAmount') }}</span>
              <strong class="bill-total">{{ formatRupiah(billTotal) }}</strong>
            </div>
            <div class="bill-due">
              <span class="bill-label">{{ $t('planDetail.billDueDate') }}</span>
              <strong v-if="billDueDate">{{ formatDisplayDate(billDueDate) }}</strong>
              <strong v-else>-</strong>
            </div>
          </div>
          <div v-if="showPeriodBreakdown" class="bill-breakdown">
            <div class="bill-breakdown-row" v-for="item in billItems" :key="`bill-${item.period}`">
              <span class="bill-period-label">
                <i class="bill-period-chip">{{ item.period }}</i>
                <span class="bill-period-copy">
                  <em>{{ item.period }} / {{ totalPeriods }}</em>
                  <small>{{ $t('planDetail.rentAmount') }}</small>
                </span>
              </span>
              <strong>{{ formatRupiah(item.rentalAmount || item.total) }}</strong>
            </div>
          </div>
          <div v-else class="bill-breakdown bill-breakdown--single">
            <div class="bill-breakdown-row">
              <span>{{ $t('planDetail.rentAmountThisPeriod') }}</span>
              <strong>{{ formatRupiah(currentBillRental) }}</strong>
            </div>
            <div class="bill-breakdown-row">
              <span>{{ $t('planDetail.rentalPeriodCount') }}</span>
              <strong>{{ currentBillPeriod }} / {{ totalPeriods }}</strong>
            </div>
          </div>
          <div v-if="billLateFee > 0" class="bill-breakdown-row bill-breakdown-row--danger">
            <span class="bill-late-fee-label">
              <i class="bill-late-fee-icon">!</i>
              <span class="bill-period-copy">
                <em>{{ $t('planDetail.lateFee') }} <button type="button" class="bill-late-fee-help" @click.stop="showLateFeeInfo">?</button></em>
                <small>{{ $t('planDetail.lateFeeForThisBill') }}</small>
              </span>
            </span>
            <strong>{{ formatRupiah(billLateFee) }}</strong>
          </div>
          <div v-if="showBillTotalDueRow" class="bill-total-row">
            <span>{{ $t('planDetail.totalAmountDue') }}</span>
            <strong>{{ formatRupiah(billTotal) }}</strong>
          </div>
        </div>
        <div class="bill-primary-action" :class="{ 'is-disabled': !canPayBill || repaySubmitting }" @click="payCurrentBill">
          {{ $t('planDetail.payRentalBill') }}
        </div>
      </section>

      <button type="button" class="bill-action-card bill-card" @click="showPlanPopup = true">
        <span class="bill-action-icon"><van-icon name="orders-o" /></span>
        <span class="bill-action-copy">
          <strong>{{ $t('planDetail.viewInstallmentPlan') }}</strong>
          <small>{{ $t('planDetail.viewInstallmentPlanDesc') }}</small>
        </span>
        <van-icon name="arrow" />
      </button>
      <button type="button" class="bill-action-card bill-card" :class="{ 'is-disabled': !canPayOff || repaySubmitting }" @click="openPayOffPopup">
        <span class="bill-action-icon"><van-icon name="balance-o" /></span>
        <span class="bill-action-copy">
          <strong>{{ $t('planDetail.payOffRemainingRent') }}</strong>
          <small>{{ $t('planDetail.payRemainingOptional') }}</small>
        </span>
        <van-icon name="arrow" />
      </button>
      <div class="bill-secure-tip"><van-icon name="shield-o" /> {{ $t('planDetail.securePaymentNote') }}</div>
      </main>
    </van-pull-refresh>

    <Popup v-model="showPlanPopup" position="bottom" round class="android-repay-plan-popup">
      <div class="android-repay-plan-sheet">
        <div class="android-repay-plan-sheet__header">
          <div class="android-repay-plan-sheet__title">{{ $t('orderPage.rentalPeriodPlan') }}</div>
          <van-icon name="cross" @click="showPlanPopup = false" />
        </div>
        <div v-if="canTerminate" class="android-repay-plan-sheet__return" @click="goTerminateFromPlanSheet">
          {{ $t('planDetail.applyForReturn') }}
        </div>
        <div class="android-repay-plan-sheet__body">
          <div class="android-repay-plan-sheet__notice">
            <span class="android-repay-plan-sheet__notice-icon"><android-order-icon name="date" :size="30" /></span>
            <div><strong>{{ $t('planDetail.repayPlanMonthlyTipTitle') }}</strong><span>{{ $t('planDetail.repayPlanMonthlyTip') }}</span></div>
          </div>
          <div v-if="!repayPlanSheetItems.length" class="android-repay-plan-sheet__state">{{ $t('planDetail.repayPlanEmpty') }}</div>
          <div v-else class="android-repay-plan-sheet__items">
            <div v-for="item in repayPlanSheetItems" :key="`sheet-${item.period}`" class="android-repay-plan-item">
              <div class="android-repay-plan-item__top">
                <strong class="android-repay-plan-item__period">{{ repayPlanPeriodText(item.period) }}</strong>
                <span :class="`android-repay-plan-item__status android-repay-plan-item__status--${item.statusClass}`">
                  <android-order-icon v-if="item.statusClass === 'unpaid'" name="radioChecked" :size="28" />
                  <van-icon v-else :name="item.statusIconName" />
                  {{ item.statusText }}
                </span>
              </div>
              <div class="android-repay-plan-item__row"><span>{{ $t('orderPage.repayPlanDueDate') }}</span><strong class="android-repay-plan-item__date">{{ formatDisplayDate(item.dueDate) }}</strong></div>
              <div class="android-repay-plan-item__row"><span>{{ $t('orderPage.repayPlanAmount') }}</span><strong class="android-repay-plan-item__amount">{{ formatRupiah(item.total) }}</strong></div>
            </div>
          </div>
          <div class="android-repay-plan-sheet__notice android-repay-plan-sheet__notice--secure">
            <span class="android-repay-plan-sheet__notice-icon"><android-order-icon name="protect" :size="30" /></span>
            <div><strong>{{ $t('orderPage.repayPlanAutoChargeTitle') }}</strong><span>{{ $t('orderPage.repayPlanAutoChargeDesc') }}</span></div>
          </div>
        </div>
      </div>
    </Popup>
    <Popup v-model="showPayOffPopup" position="bottom" round class="android-payoff-popup">
      <div class="android-payoff-sheet">
        <div class="android-payoff-sheet__header">
          <div>
            <h3>{{ $t('planDetail.payOffRemainingRent') }}</h3>
            <p>{{ $t('planDetail.payRemainingOptional') }}</p>
          </div>
          <van-icon name="cross" @click="showPayOffPopup = false" />
        </div>
        <div class="android-payoff-sheet__body">
          <div class="android-payoff-sheet__notice">
            <span class="android-payoff-sheet__notice-icon"><van-icon name="orders-o" /></span>
            <span>{{ $t('planDetail.payOffIncludesNote') }}</span>
          </div>
          <div class="android-payoff-sheet__section-title">{{ $t('planDetail.remainingRentalPeriods') }}</div>
          <div class="android-payoff-sheet__items">
            <div v-for="item in payOffSheetItems" :key="`payoff-${item.period}`" class="android-repay-plan-item">
              <div class="android-repay-plan-item__top">
                <strong class="android-repay-plan-item__period">{{ repayPlanPeriodText(item.period) }}</strong>
                <span :class="`android-repay-plan-item__status android-repay-plan-item__status--${item.statusClass}`">{{ item.statusText }}</span>
              </div>
              <div class="android-repay-plan-item__row"><span>{{ $t('orderPage.repayPlanDueDate') }}</span><strong class="android-repay-plan-item__date">{{ formatDisplayDate(item.dueDate) }}</strong></div>
              <div class="android-repay-plan-item__row"><span>{{ $t('orderPage.repayPlanAmount') }}</span><strong class="android-repay-plan-item__amount">{{ formatRupiah(item.payOffRentalAmount) }}</strong></div>
            </div>
          </div>
          <div class="android-payoff-summary">
            <div><span>{{ remainingRentPeriodsText }}</span><strong>{{ formatRupiah(payOffRentalAmount) }}</strong></div>
            <div v-if="payOffLateFee > 0" class="android-payoff-summary__late-fee">
              <span>
                {{ $t('planDetail.lateFee') }}
                <button type="button" class="android-payoff-summary__late-fee-help" @click.stop="showLateFeeInfo">?</button>
              </span>
              <strong>{{ formatRupiah(payOffLateFee) }}</strong>
            </div>
            <div class="android-payoff-summary__total"><span>{{ $t('planDetail.totalAmountDue') }}</span><strong>{{ formatRupiah(remainingPayAmount) }}</strong></div>
          </div>
        </div>
        <div class="android-payoff-sheet__action" :class="{ 'is-disabled': !canPayOff || repaySubmitting }" @click="confirmPayOff">{{ $t('planDetail.payAmountFormat', { amount: formatRupiah(remainingPayAmount) }) }}</div>
        <div class="android-payoff-sheet__secure"><van-icon name="shield-o" /> {{ $t('planDetail.payOffSecureNote') }}</div>
      </div>
    </Popup>
    <Popup v-model="showPayUrlPopup" position="bottom">
      <div class="pay-url-popup">
        <div class="pay-url-popup__close" @click="showPayUrlPopup = false">
          <img src="@/assets/img/close.png" alt="">
        </div>
        <div class="pay-url-popup__title">{{ $t('orderPage.payUrlTitle') }}</div>
        <div class="pay-url-popup__desc">{{ $t('orderPage.payUrlDesc') }}</div>
        <div class="pay-url-popup__footer">
          <div class="btn-primary btn-normal" @click="goToPayUrl">{{ $t('orderPage.goToPay') }}</div>
          <div
            class="pay-url-popup__completed-btn"
            :class="{ 'pay-url-popup__completed-btn--disabled': checkingPaymentStatus }"
            @click="checkRepayPaymentStatus"
          >
            {{ $t('orderPage.paymentCompletedButton') }}
          </div>
        </div>
      </div>
    </Popup>
    <Popup v-model="showLateFeePopup" position="center" class="late-fee-popup-wrapper" :close-on-click-overlay="true">
      <div class="late-fee-popup">
        <button type="button" class="late-fee-popup__close" @click="showLateFeePopup = false">×</button>
        <div class="late-fee-popup__icon">i</div>
        <div class="late-fee-popup__title">{{ $t('planDetail.lateFeeRuleTitle') }}</div>
        <div class="late-fee-popup__desc">{{ lateFeeRuleDesc }}</div>
        <button type="button" class="late-fee-popup__confirm" @click="showLateFeePopup = false">{{ $t('confirm') }}</button>
      </div>
    </Popup>
    <PaymentPopup ref="paymentPopup"/>
  </div>
</template>
<script>
import { mapActions } from 'vuex';
import { Icon, Popup, PullRefresh } from 'vant';
import PaymentPopup from '@/components/PaymentPopup/index.vue';
import { copyText } from '@/utils/clipboard';
import { queryRepayPlan, queryOrderDetail } from '@/api/order';
import { queryPaymentInfo } from '@/api/payment';
import { queryBreachTrial } from '@/api/breach';
import { RENTAL_PLAN_STATUS, ORDER_STATUS, PAYMENT_STATUS } from '@/common/constants';
import routerBack from '@/mixins/routerBack';
import AndroidOrderIcon from '@/components/AndroidOrderIcon.vue';
import { formatRentalDate } from '@/filters';
import { formatRupiahAmount } from '@/utils/money';
export default {
  name: 'RentalplanDetail',
  mixins: [routerBack],
  components: {
    Popup,
    'van-icon': Icon,
    'van-pull-refresh': PullRefresh,
    PaymentPopup,
    AndroidOrderIcon,
  },
  computed: {
    title() {
      return this.$t('planDetail.billDetails');
    },
    priceUint() {
      return this.$t('priceUnit');
    },
    skuStr() {
      if (!this.orderDetail) {
        return '';
      }
      let { skuInfo, planIndex, planUnit } = this.orderDetail;
      return skuInfo + ', ' + planIndex + ', ' + this.$t(`cycle.${planUnit}`)
    },
    displaySkuText() {
      const info = this.displayOrderInfo || {};
      const parts = [
        info.skuInfo,
        info.planIndex,
        info.planUnit ? this.$t(`cycle.${info.planUnit}`) : '',
      ].filter(item => item !== null && item !== undefined && String(item).trim() !== '');
      return parts.length ? parts.join(' · ') : this.skuStr.replace(/,\s*/g, ' · ');
    },
    canTerminate() {
      if (!this.orderDetail) {
        return false;
      }
      return [ORDER_STATUS.RETURN, ORDER_STATUS.BREACH].includes(this.orderDetail.status);
    },
    remainingPlanList() { // 剩余计划列表
      return this.planList.filter(item => !this.isPaidOrClosed(item));
    },
    payOffSheetItems() {
      return this.remainingPlanList.map(item => ({
        ...item,
        dueDate: this.resolveRepayPlanDueDate(item),
        payOffRentalAmount: this.displayPayOffRentalAmount(item),
        statusClass: this.repayPlanStatusClass(item.status),
        statusText: this.repayPlanStatusText(item.status)
      }));
    },
    payOffRentalAmount() {
      return this.payOffSheetItems.reduce((total, item) => {
        return parseFloat(((total * 100) + (item.payOffRentalAmount * 100)) / 100);
      }, 0);
    },
    payOffLateFee() {
      return this.payOffSheetItems.reduce((total, item) => {
        return parseFloat(((total * 100) + ((Number(item.overdueAmount) || 0) * 100)) / 100);
      }, 0);
    },
    remainingRentPeriodsText() {
      const count = this.payOffSheetItems.length;
      return this.$t(count === 1 ? 'planDetail.remainingRentOnePeriod' : 'planDetail.remainingRentManyPeriods', { count });
    },
    totalRemainingAmount() { // 总剩余金额
      return this.remainingPlanList.reduce((total, item) => {
        return parseFloat(((total * 100) + (item.total * 100)) / 100);
      }, 0)
    },
    canRepay() {
      if (!this.orderDetail) {
        return false;
      }
      return [ORDER_STATUS.DELIVERY,ORDER_STATUS.LOGISTICS, ORDER_STATUS.RECEIVE, ORDER_STATUS.RETURN].includes(this.orderDetail.status);
    },
    canPayOff() {
      return this.canRepay && this.remainingPayPeriods.length > 0 && this.remainingPayAmount > 0;
    },
    canPayNow() {
      return this.canRepay && this.selectPeriodList.length > 0;
    },
    selectPeriodList() {
      return this.planList.filter(item => item.selected);
    },
    repayPlanSheetItems() {
      return this.planList.map(item => ({
        ...item,
        dueDate: this.resolveRepayPlanDueDate(item),
        statusClass: this.repayPlanStatusClass(item.status),
        statusText: this.repayPlanStatusText(item.status),
        statusIconName: this.repayPlanStatusIcon(item.status)
      }));
    },
    payAmount() {
      return this.selectPeriodList.reduce((total, item) => {
        return parseFloat(((total * 100) + (item.total * 100)) / 100);
      }, 0);
    },
    showPaidTime() {
      if (!this.orderDetail) {
        return false;
      }
      return ![ORDER_STATUS.PAYMENT, ORDER_STATUS.DELIVERY,ORDER_STATUS.LOGISTICS, ORDER_STATUS.RECEIVE].includes(this.orderDetail.status);
    },
    displayOrderInfo() {
      return this.planDetail?.orderInfo || this.orderDetail || {};
    },
    displayOrderNumber() {
      return this.displayOrderInfo.rentalOrderId || this.orderId || '';
    },
    currentBillDetail() {
      return this.planDetail?.billDetail || {};
    },
    hasBackendBillDetail() {
      return !!this.planDetail?.billDetail;
    },
    currentPayInfo() {
      return this.planDetail?.payInfo || {};
    },
    payPeriods() {
      const payPeriods = Array.isArray(this.currentPayInfo.periods) ? this.currentPayInfo.periods : [];
      const detailPeriods = Array.isArray(this.currentBillDetail.effectivePeriods)
        ? this.currentBillDetail.effectivePeriods
        : (Array.isArray(this.currentBillDetail.periods) ? this.currentBillDetail.periods : []);
      return (payPeriods.length ? payPeriods : detailPeriods).filter(period => Number(period) > 0);
    },
    payAmountForBill() {
      return Number(this.currentPayInfo.amount) || this.billTotal;
    },
    currentBillPeriods() {
      const detailPeriods = Array.isArray(this.currentBillDetail.effectivePeriods)
        ? this.currentBillDetail.effectivePeriods
        : (Array.isArray(this.currentBillDetail.periods) ? this.currentBillDetail.periods : []);
      return (detailPeriods.length ? detailPeriods : this.payPeriods).filter(period => Number(period) > 0);
    },
    billItems() {
      const periods = this.currentBillPeriods;
      const items = this.planList.filter(item => periods.includes(Number(item.period)));
      const fallbackItem = this.planList.find(item => this.getCheckCanSelected(item));
      return (items.length ? items : (fallbackItem ? [fallbackItem] : [])).sort((a, b) => Number(a.period) - Number(b.period));
    },
    showPeriodBreakdown() {
      return this.billItems.length > 1;
    },
    showBillTotalDueRow() {
      return this.showPeriodBreakdown;
    },
    totalPeriods() {
      return Number(this.currentBillDetail.totalPeriods) || Math.max(...this.planList.map(item => Number(item.period) || 0), 0);
    },
    currentBillPeriod() {
      return Number(this.currentBillDetail.period) || Number(this.billItems[0]?.period) || 0;
    },
    currentBillRental() {
      const fallbackTotal = Number(this.billItems[0]?.total) || 0;
      const fallbackLateFee = Number(this.billItems[0]?.overdueAmount) || 0;
      return Number(this.currentBillDetail.rentalAmount) || Number(this.billItems[0]?.rentalAmount) || Math.max(fallbackTotal - fallbackLateFee, 0);
    },
    billLateFee() {
      return Number(this.currentBillDetail.overdueAmount) || this.billItems.reduce((total, item) => total + (Number(item.overdueAmount) || 0), 0);
    },
    billTotal() {
      return Number(this.currentBillDetail.total) || this.billItems.reduce((total, item) => total + (Number(item.total) || 0), 0);
    },
    billDueDate() {
      return this.currentBillDetail.dueDate || (!this.hasBackendBillDetail ? this.billItems[0]?.repayDay : '') || '';
    },
    canPayBill() {
      return !this.billFullySettled && (this.currentPayInfo.canPay === true || this.currentBillDetail.canPay === true) && this.payPeriods.length > 0;
    },
    remainingPayInfo() {
      return this.planDetail?.remainingPayInfo || {};
    },
    remainingPayPeriods() {
      const periods = Array.isArray(this.remainingPayInfo.periods) ? this.remainingPayInfo.periods : [];
      return (periods.length ? periods : this.remainingPlanList.map(item => item.period)).filter(period => Number(period) > 0);
    },
    remainingPayAmount() {
      return Number(this.remainingPayInfo.amount) || this.totalRemainingAmount;
    },
    allRepayPlansClosed() {
      return this.planList.length > 0 && this.planList.every(item => this.isPaidOrClosed(item));
    },
    billFullySettled() {
      if (this.allRepayPlansClosed) return true;
      return this.hasBackendBillDetail
        && this.currentPayInfo.canPay !== true
        && this.currentBillDetail.canPay !== true
        && this.payPeriods.length === 0
        && this.remainingPayPeriods.length === 0
        && this.remainingPayAmount <= 0;
    },
    billStatusClass() {
      if (this.billFullySettled) return 'paid';
      const status = String(this.currentBillDetail.status || this.billItems[0]?.status || '').trim().toUpperCase();
      if (['SETTLED', 'PAID', 'SUCCESS'].includes(status)) return 'paid';
      if (status === 'OVERDUE') return 'overdue';
      if (['UPCOMING', 'FROZEN', 'CANCELLED'].includes(status)) return 'upcoming';
      return 'unpaid';
    },
    billStatusText() {
      return this.$t(`planDetail.status.${this.billStatusClass}`);
    },
    lateFeeDailyAmount() {
      return 30000;
    },
    lateFeeRuleDesc() {
      return this.$t('planDetail.lateFeeRuleDesc', {
        amount: this.formatRupiah(this.lateFeeDailyAmount)
      });
    },
  },
  data() {
    return {
      orderId: null,
      orderDetail: null,
      planDetail: null,
      refreshing: false,
      depositAmount: 0,
      planList: [],
      showPayUrlPopup: false,
      payUrl: '',
      repayOrderId: '',
      checkingPaymentStatus: false,
      loading: false,
      loadError: false,
      loadErrorMessage: '',
      repaySubmitting: false,
      showPlanPopup: false,
      showPayOffPopup: false,
      showLateFeePopup: false,
      autoPay: false,
      autoPayPeriods: [],
      autoPayAmount: 0,
      autoPayConsumed: false,
    }
  },
  mounted() {
    this.orderId = this.$route.query.orderId;
    this.autoPay = this.$route.query.autoPay === 'true';
    this.autoPayPeriods = String(this.$route.query.periods || '')
      .split(',')
      .map(period => Number(period))
      .filter(period => period > 0);
    this.autoPayAmount = Number(this.$route.query.amount || 0);
    if (this.orderId) {
      this.loadPage();
    } else {
      this.loadError = true;
    }
  },
  methods: {
    ...mapActions({
      "doRepay": 'orders/doRepay',
    }),
    async onRefresh() {
      try {
        await this.loadPage();
      } finally {
        this.refreshing = false;
      }
    },
    async loadPage() {
      if (!this.orderId || this.loading) return;
      this.loading = true;
      this.loadError = false;
      this.loadErrorMessage = '';
      this.orderDetail = null;
      this.planDetail = null;
      this.planList = [];
      try {
        await Promise.all([
          this.loadRequest('repay plan', () => this.getRepayPlanDetail()),
          this.loadRequest('order detail', () => this.getOrderDetail())
        ]);
        this.triggerAutoPay();
      } catch (e) {
        const source = e?.source || 'rental plan detail';
        const error = e?.cause || e;
        const message = error?.message || error?.response?.data?.message || error?.response?.statusText || String(error || '');
        this.loadErrorMessage = message;
        console.error('Rental plan detail request failed', { source, error });
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    async loadRequest(source, request) {
      try {
        const response = await request();
        if (!response || response.code !== 'Success') {
          const error = new Error(response?.message || `${source} returned an unsuccessful response`);
          error.response = response;
          throw error;
        }
        return response;
      } catch (error) {
        const wrappedError = error instanceof Error ? error : new Error(String(error || 'Request failed'));
        wrappedError.source = source;
        wrappedError.cause = error;
        throw wrappedError;
      }
    },
    async getOrderDetail() {
      const res = await queryOrderDetail({ orderId: this.orderId });
        if (res.code === 'Err.ErrOrderIllegal') {
          this.$toast(res.message);
          setTimeout(() => {
            this.$router.replace({ name: 'Index' });
          }, 3000);
          return res;
        }
        if (res.code === 'Success') {
          this.orderDetail = res.data;
        }
        return res;
    },
    getCheckCanSelected({ status }) {
      if (!status) {
        return false;
      }
      switch (status) {
        case RENTAL_PLAN_STATUS.PENDING:
        case RENTAL_PLAN_STATUS.OVERDUE:
          return true;
      }
    },
    getStatusText({ status }) {
      switch (status) {
        case RENTAL_PLAN_STATUS.PENDING:
        case RENTAL_PLAN_STATUS.FROZEN:
        case RENTAL_PLAN_STATUS.CANCELLED:
          return 'due';
        case RENTAL_PLAN_STATUS.SETTLED:
          return 'paid';
        case RENTAL_PLAN_STATUS.OVERDUE:
          return 'overdue';
        default:
          return '';
      }
    },
    async getRepayPlanDetail() {
      const res = await queryRepayPlan({ orderId: this.orderId });
        if (res.code === 'Err.ErrOrderIllegal') {
          this.$toast(res.message);
          setTimeout(() => {
            this.$router.replace({ name: 'Index' });
          }, 3000);
          return res;
        }
        if (res.code === 'Success') {
          this.planDetail = res.data;
          this.depositAmount = res.data.depositAmount || 0;
          // 格式化列表数据
          this.planList = (res.data.planList || []).map((item, index) => {
            let statusText = this.getStatusText(item);
            if (index === 0) {
              item.total = Math.round(((Number(item.total) || 0) * 100 + this.depositAmount * 100)) / 100;
            }
            return {
              ...item,
              statusClass: statusText,
              statusText: this.$t(`planDetail.status.${statusText}`),
              canSelected: this.getCheckCanSelected(item),
              selected: false
            }
          });
        }
        return res;
    },
    onClickLeft() {
      this.goBack()
    },
    async copyOrderNumber() {
      if (!this.displayOrderNumber) return;
      const success = await copyText(this.displayOrderNumber);
      this.$toast(this.$t(success ? 'planDetail.copySuccess' : 'planDetail.copyFailed'));
    },
    showLateFeeInfo() {
      this.showLateFeePopup = true;
    },
    repayPlanPeriodSuffix(period) {
      const locale = this.$i18n?.locale || localStorage.getItem('language') || 'en';
      if (locale === 'id') return '';
      const value = Math.max(Number(period) || 0, 0);
      if (value >= 11 && value <= 13) return 'th';
      return {1: 'st', 2: 'nd', 3: 'rd'}[value % 10] || 'th';
    },
    repayPlanPeriodText(period) {
      return this.$t('orderPage.repayPlanPeriodFormat', {
        period,
        suffix: this.repayPlanPeriodSuffix(period)
      });
    },
    repayPlanStatusClass(status) {
      const normalized = String(status || '').trim().toUpperCase();
      if (['SETTLED', 'PAID', 'SUCCESS'].includes(normalized)) return 'paid';
      if (normalized === 'OVERDUE') return 'overdue';
      if (['PENDING', 'UNPAID'].includes(normalized)) return 'unpaid';
      return 'upcoming';
    },
    repayPlanStatusText(status) {
      const statusClass = this.repayPlanStatusClass(status);
      if (statusClass === 'unpaid') return this.$t('rentalplan.unpaid');
      if (statusClass === 'upcoming') return this.$t('planDetail.status.upcoming');
      return this.$t(`planDetail.status.${statusClass}`);
    },
    repayPlanStatusIcon(status) {
      const statusClass = this.repayPlanStatusClass(status);
      if (statusClass === 'paid') return 'checked';
      if (statusClass === 'overdue') return 'warning-o';
      if (statusClass === 'unpaid') return 'circle';
      return 'clock-o';
    },
    goTerminateFromPlanSheet() {
      this.showPlanPopup = false;
      this.goTerminate();
    },
    formatRupiah(amount) {
      return `Rp${formatRupiahAmount(amount) || '0'}`;
    },
    formatDisplayDate(value) {
      return formatRentalDate(value);
    },
    formatDisplayDateTime(value) {
      return formatRentalDate(value);
    },
    resolveRepayPlanDueDate(item = {}) {
      const candidates = [
        item.paidTime,
        item.paid_time,
        item.repayDay,
        item.repay_day,
        item.dueDate,
        item.due_date,
        item.billDate,
        item.bill_date,
        item.planDate,
        item.plan_date,
        item.repayDate,
        item.repay_date
      ];
      return candidates.find(value => this.parseDisplayDate(value)) || '';
    },
    parseDisplayDate(value) {
      if (value === null || value === undefined || String(value).trim() === '' || String(value).trim() === '0') return null;
      if (typeof value === 'number' || /^\d+$/.test(String(value))) {
        const numeric = Number(value);
        const timestamp = numeric < 1000000000000 ? numeric * 1000 : numeric;
        const date = new Date(timestamp);
        return Number.isNaN(date.getTime()) || date.getFullYear() < 2001 ? null : date;
      }
      const normalized = String(value).trim().replace(/-/g, '/');
      const date = new Date(normalized);
      return Number.isNaN(date.getTime()) || date.getFullYear() < 2001 ? null : date;
    },
    payCurrentBill() {
      if (!this.canPayBill || this.repaySubmitting) return;
      this.goRepayCheckout({ periods: this.payPeriods, amount: this.payAmountForBill });
    },
    triggerAutoPay() {
      if (!this.autoPay || this.autoPayConsumed || this.repaySubmitting) return;
      this.autoPayConsumed = true;
      const periods = this.autoPayPeriods.length ? this.autoPayPeriods : this.payPeriods;
      const amount = this.payAmountForBill || this.autoPayAmount;
      if (!periods.length || !amount) return;
      this.goRepayCheckout({ periods, amount });
    },
    openPayOffPopup() {
      if (!this.canPayOff || this.repaySubmitting) return;
      this.showPayOffPopup = true;
    },
    isPaidOrClosed(item) {
      const status = String(item?.status || '').trim().toUpperCase();
      return ['SETTLED', 'PAID', 'SUCCESS', 'CLOSED', 'CANCELLED'].includes(status);
    },
    displayPayOffRentalAmount(item = {}) {
      const rentalAmount = Number(item.rentalAmount) || 0;
      if (rentalAmount > 0) return rentalAmount;
      return Math.max((Number(item.total) || 0) - (Number(item.overdueAmount) || 0), 0);
    },
    confirmPayOff() {
      if (!this.canPayOff || this.repaySubmitting) return;
      this.showPayOffPopup = false;
      this.payOff();
    },
    goTerminate() {
      if (String(this.orderDetail?.breachOrderStatus || '').trim() !== '') {
        this.$router.push({
          name: 'Terminate',
          query: {
            orderId: this.orderId
          }
        });
        return
      }
      queryBreachTrial({
        rentalOrderId: this.orderId,
      }).then(res => {
        let penaltyFee = 0;
        if (res?.code === 'Success' && res.data?.allowBreach) {
          penaltyFee = res.data.penaltyFee;
          this.$router.push({
            name: 'Terminate',
            query: {
              orderId: this.orderId,
              penaltyFee
            }
          });
        } else {
          this.$toast(res.data?.reason || res.message || this.$t('systemError'));
        }
      })
    },

    togglePlan(plan, index) {
      if (!plan.canSelected) {
        return;
      }
      // 判断前/后一个是否选择(只能连续选择)
      let prevPlan = this.planList[index - 1];
      let nextPlan = this.planList[index + 1];
      if ((!plan.selected && prevPlan && !prevPlan.selected && prevPlan.canSelected) || (plan.selected && nextPlan?.selected)) {
        this.$toast(this.$t('planDetail.selectPrev'));
        return;
      }

      this.$set(this.planList[index], 'selected', !plan.selected);
    },
    /**
     * 结清
     */
    payOff() {
      if (!this.canPayOff) {
        return;
      }
      this.goRepayCheckout({
        periods: this.remainingPayPeriods,
        amount: this.remainingPayAmount
      });
    },
    /**
     * 按期还
     */
    payByPeriod() {
      if (!this.canPayNow) {
        return;
      }

      if (this.selectPeriodList.length === 0) {
        return;
      }
      let isIdsContinuous = this.selectPeriodList.every((item, index) => {
        return index === 0 || item.period === this.selectPeriodList[index - 1].period + 1;
      });
      if (!isIdsContinuous) {
        this.$toast(this.$t('planDetail.selectPrev'));
        return;
      }

      this.goRepayCheckout({
        periods: this.selectPeriodList.map(item => item.period),
        amount: this.payAmount
      });
      this.showPlanPopup = false;
    },
    goRepayCheckout({ periods, amount } = {}) {
      const normalizedPeriods = (Array.isArray(periods) ? periods : [])
        .map(period => Number(period))
        .filter(period => period > 0);
      const normalizedAmount = Number(amount) || 0;
      if (!normalizedPeriods.length || normalizedAmount <= 0) {
        return;
      }
      this.$router.push({
        name: 'RepayCheckout',
        query: {
          orderId: this.orderId,
          periods: normalizedPeriods.join(','),
          amount: normalizedAmount,
          sourcePage: 'detail'
        }
      });
    },
    /***
     * 确认支付
     */
    async doPay({ periods, amount }) {
      if (!periods || periods.length === 0 || this.repaySubmitting) {
        return;
      }
      this.repaySubmitting = true;
      try {
        const res = await this.doRepay({
          orderId: this.orderId,
          periods,
          amount,
          returnUrl: location.pathname + location.search,
        });
        if (res?.code === 'Success' && res.data?.payContent) {
          this.$refs.paymentPopup.open({
            payContent: res.data.payContent,
            amount: res.data.amount || amount,
          });
        } else if (res?.code === 'Success' && res.data?.payUrl) {
          this.payUrl = res.data.payUrl;
          this.repayOrderId = res.data.repayOrderId;
          this.showPayUrlPopup = true;
        } else if (res?.code === 'Err.RepayAmountMismatch') {
          res.message && this.$toast(res.message);
          setTimeout(() => {
            this.$router.reload();
          }, 2000);
        } else if (res && res.message) {
          this.$toast(res.message || this.$t('systemError'));
        }
      } finally {
        this.repaySubmitting = false;
      }
    },
    goToPayUrl() {
      if (!this.payUrl) {
        return;
      }
      window.open(this.payUrl, '_blank', 'noopener,noreferrer');
    },
    async checkRepayPaymentStatus() {
      if (this.checkingPaymentStatus || !this.repayOrderId) {
        return;
      }
      this.checkingPaymentStatus = true;
      try {
        const res = await queryPaymentInfo({ orderId: this.repayOrderId });
        if (res?.code !== 'Success') {
          this.$toast(res?.message || this.$t('systemError'));
          return;
        }
        if (res.data?.state !== PAYMENT_STATUS.SUCCESS) {
          this.$toast(this.$t('orderPage.paymentNotCompleted'));
          return;
        }

        this.showPayUrlPopup = false;
        await Promise.all([
          this.getRepayPlanDetail(),
          this.getOrderDetail(),
        ]);
      } catch (e) {
        this.$toast(this.$t('systemError'));
      } finally {
        this.checkingPaymentStatus = false;
      }
    }
  }
}
</script>
<style lang="less">
.notes-title {
  font-size: 32px;
  line-height: 37px;
  font-weight: 500;
}
.notes-content {
  font-size: 26px;
  line-height: 30px;
  margin-top: 30px;
  color: var(--text-color-gray);
}
.notes-content p {
  margin-top: 20px;
}
.notes-content p:first-child {
  margin-top: 0;
}

.pay-url-popup {
  position: relative;
  padding: 48px 40px 44px;
  box-sizing: border-box;
  text-align: center;

  &__close {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 36px;
    height: 36px;

    img {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__title {
    font-size: 34px;
    line-height: 48px;
    font-weight: 500;
    color: var(--text-color-black);
  }

  &__desc {
    margin-top: 20px;
    font-size: 26px;
    line-height: 38px;
    color: var(--text-color-gray);
  }

  &__footer {
    margin-top: 40px;
  }

  &__completed-btn {
    height: 80px;
    margin-top: 20px;
    border: 2px solid var(--primary-color);
    border-radius: var(--border-radius-base);
    box-sizing: border-box;
    font-size: 28px;
    line-height: 76px;
    color: var(--primary-color);

    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}
</style>
<style lang="less" scoped>
.rentalplan-detail {
  min-height: 100vh;
  padding-top: 88px;
  padding-bottom: 160px;
  box-sizing: border-box;
  background: #ffffff;

  &__notes-section {
    background: var(--bg-color);
    padding: 24px 30px;
  }

  &__notes {
    background: #ffffff;
    padding: 30px 40px;
  }
}

.order-detail {
  padding: 30px 30px 50px 30px;
  background-color: var(--bg-color);
  position: relative;

  .r-t-btn {
    font-size: 26px;
    color: var(--primary-color);
    line-height: 37px;
    position: absolute;
    right: 16px;
    top: 12px;
    text-decoration: underline;
  }

  .t1 {
    width: fit-content;
    font-size: 26px;
    line-height: 37px;
    text-align: left;
  }

  .total-due-wrapper {
    margin-top: 30px;
  }

  .total-due-grid {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    align-items: end;
  }

  .total-due-grid .r-b-btn {
    grid-column: 2;
    grid-row: 2;
  }

  .t2 {
    font-size: 60px;
    line-height: 69px;
    text-align: center;
  }

  .r-b-btn {
    min-width: 132px;
    padding: 0 12px;
    height: fit-content;
    box-sizing: border-box;
    font-size: 26px;
    color: var(--primary-color);
    line-height: 38px;
    border-radius: 22px;
    border: 2px solid #2547F1;
    background: #ffffff;
    //position: absolute;
    //right: 46px;
    //bottom: 20px;

    width: fit-content;
    text-align: center;
    font-weight: 500;

    &.disabled {
      background-color: #D8D8D8;
      border: 2px solid #D8D8D8;
      color: #ffffff;
    }
  }
}

.h-text {
  font-size: 32px;
  line-height: 37px;
  color: var(--text-color-black);
}

.g-text {
  font-size: 26px;
  line-height: 28px;
  color: var(--text-color-gray);
}

.p-icon {
  display: flex;
  justify-content: right;

  >div {
    padding: 3px 12px;
    font-size: 26px;
    line-height: 26px;
    border-radius: 8px;
    color: #fff;
    width: auto;

    &.paid {
      background-color: #68AA36;
    }

    &.overdue {
      background-color: #FF5A00;
    }

    &.due {
      background-color: #666666;
    }
  }
}

.plan-detail {
  padding: 60px 60px 0 60px;
}

.rentalplan-list {
  padding-bottom: 50px;

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: start;
    padding-bottom: 60px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 26px;
      left: 212px;
      height: 100%;
      width: 0;
      border-right: 1px dashed var(--text-color-gray);
    }

    &:last-child {
      padding-bottom: 0;

      &::before {
        display: none;
      }
    }
  }
}

.left {
  text-align: right;
  white-space: nowrap;
  width: 180px;
}

.dot {
  width: 70px;
  height: 70px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 6px;
    left: 50%;
    transform: translateX(-50%);
    width: 22px;
    height: 22px;
    background: url('../../assets/img/rentalplan/dot_disabled.png') no-repeat center;
    background-size: cover;
  }

  &.can_selected {
    &::before {
      background: #ffffff url('../../assets/img/rentalplan/dot_un.png') no-repeat center;
      background-size: cover;
    }
  }

  &.selected {
    &::before {
      background: #ffffff url('../../assets/img/rentalplan/dot_ac.png') no-repeat center;
      background-size: cover;
    }
  }
}

.right {
  flex: 1;
}

.plan-footer {
  position: fixed;
  padding: 30px 60px 30px 60px;
  width: 100%;
  bottom: 0;
  left: 0;
  z-index: 1;
  background: #ffffff;

  .price {
    font-size: 32px;
    font-weight: 500;
  }

  .btn {
    width: 320px;
  }
}
</style>

<style lang="less" scoped>
/* Final Android Bill Details override. Values are 750-design px and converted by postcss-pxtorem(rootValue:75). */
.rentalplan-detail {
  min-height: 100vh;
  padding-top: 96px;
  padding-bottom: 32px;
  background: #F4F8FF;
  color: #070D3F;

  ::v-deep .van-nav-bar { height: 96px; background: #FFFFFF; }
  ::v-deep .van-nav-bar__content { height: 96px; }
  ::v-deep .van-nav-bar__title { color: #070D3F; font-size: 32px; line-height: 44px; font-weight: 500; }
  ::v-deep .van-nav-bar .van-icon { color: #070D3F; font-size: 44px; }
}

.bill-detail-content { padding: 16px; }
.bill-card { box-sizing: border-box; width: 100%; margin-bottom: 16px; padding: 20px; border: 2px solid #E5EAF3; border-radius: 20px; background: #FFFFFF; box-shadow: none; }
.bill-detail-error { display: flex; min-height: calc(100vh - 128px); align-items: center; }
.bill-error-card { margin: 0; padding: 48px 32px 40px; text-align: center; }
.bill-error-icon { display: inline-flex; align-items: center; justify-content: center; width: 88px; height: 88px; border-radius: 50%; color: #F97316; background: #FFF4EC; font-size: 48px; }
.bill-error-title { margin-top: 24px; color: #070D3F; font-size: 30px; line-height: 42px; font-weight: 500; }
.bill-error-desc { margin: 12px auto 0; max-width: 560px; color: #6B7280; font-size: 24px; line-height: 34px; word-break: break-word; }
.bill-error-retry,
.bill-error-back { display: block; width: 100%; padding: 0; border: 0; background: transparent; font: inherit; }
.bill-error-retry { height: 76px; margin-top: 32px; border-radius: 16px; color: #FFFFFF; background: #0B45FF; font-size: 26px; line-height: 76px; font-weight: 500; }
.bill-error-back { width: auto; min-width: 120px; margin: 20px auto 0; color: #6B7280; font-size: 24px; line-height: 34px; }
.bill-detail-skeleton { pointer-events: none; }
.bill-skeleton-card { overflow: hidden; }
.bill-skeleton-row,
.bill-skeleton-product,
.bill-skeleton-grid { display: flex; align-items: center; justify-content: space-between; }
.bill-skeleton-line,
.bill-skeleton-pill,
.bill-skeleton-image,
.bill-skeleton-block,
.bill-skeleton-button,
.bill-skeleton-action {
  display: block;
  border-radius: 999px;
  background: linear-gradient(90deg, #EEF3FA 0%, #F8FBFF 48%, #EEF3FA 100%);
  background-size: 200% 100%;
  animation: billSkeleton 1.2s ease-in-out infinite;
}
.bill-skeleton-line { width: 100%; height: 24px; }
.bill-skeleton-line--order { width: 300px; max-width: 58%; }
.bill-skeleton-line--title { width: 82%; height: 30px; }
.bill-skeleton-line--subtitle { width: 62%; height: 24px; margin-top: 18px; }
.bill-skeleton-line--section { width: 260px; height: 34px; border-radius: 12px; }
.bill-skeleton-line--label { width: 180px; height: 22px; }
.bill-skeleton-line--amount { width: 220px; height: 38px; margin-top: 16px; margin-bottom: 26px; }
.bill-skeleton-pill { width: 104px; height: 42px; }
.bill-skeleton-image { flex: 0 0 144px; width: 144px; height: 144px; border-radius: 16px; }
.bill-skeleton-copy { display: flex; flex: 1; min-width: 0; margin-left: 16px; flex-direction: column; }
.bill-skeleton-grid { gap: 16px; }
.bill-skeleton-block { flex: 1; height: 78px; border-radius: 16px; }
.bill-skeleton-summary { margin-top: 20px; padding: 20px 16px 16px; border-radius: 16px; background: #F6F9FF; }
.bill-skeleton-summary .bill-skeleton-line:not(:first-child):not(.bill-skeleton-line--amount) { width: 100%; height: 24px; margin-top: 20px; }
.bill-skeleton-button { width: 100%; height: 92px; margin-top: 16px; border-radius: 16px; }
.bill-skeleton-action { height: 92px; border-radius: 20px; }
@keyframes billSkeleton {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
.bill-card__header,
.bill-product-row,
.bill-time-grid,
.bill-total-row,
.bill-breakdown-row,
.bill-installment__row,
.bill-payoff-summary > div { display: flex; align-items: center; justify-content: space-between; }
.bill-order-number { min-width: 0; display: flex; align-items: center; color: #070D3F; font-size: 26px; line-height: 36px; font-weight: 500; }
.bill-copy { position: relative; flex-shrink: 0; width: 30px; height: 34px; margin-left: 8px; box-sizing: border-box; cursor: pointer; }
.bill-copy::before,
.bill-copy::after { position: absolute; width: 20px; height: 26px; border: 3px solid #0B45FF; border-radius: 3px; box-sizing: border-box; content: ''; }
.bill-copy::before { top: 0; right: 0; background: #FFFFFF; }
.bill-copy::after { left: 0; bottom: 0; background: #FFFFFF; }
.bill-status { flex-shrink: 0; padding: 6px 14px; border-radius: 999px; font-size: 22px; line-height: 30px; font-weight: 400; }
.bill-status.due,
.bill-status.unpaid { color: #FF7A00; background: #FFF4EC; }
.bill-status.upcoming { color: #6B7280; background: #EFF1F5; }
.bill-status.paid { color: #12C978; background: #EAF8F0; }
.bill-status.overdue { color: #FF2D16; background: #FFF1F1; }
.bill-divider { height: 2px; margin: 20px 0; background: #E5EAF3; }
.bill-product-image { flex: 0 0 144px; width: 144px; height: 144px; border-radius: 16px; background: #F6F8FC center/contain no-repeat; }
.bill-product-copy { min-width: 0; flex: 1; margin-left: 16px; }
.bill-product-name { color: #070D3F; font-size: 28px; line-height: 38px; font-weight: 500; }
.bill-product-sku,
.bill-time-block > span,
.bill-label,
.bill-breakdown-row span,
.bill-installment__row span,
.bill-payoff-summary span { color: #6B7280; font-size: 24px; line-height: 32px; font-weight: 400; }
.bill-summary-head .bill-label { display: block; line-height: 24px; margin-bottom: 15px}
.bill-time-grid { gap: 16px; }
.bill-time-block { display: flex; flex: 1; min-width: 0; flex-direction: column; align-items: center; text-align: center; }
.bill-time-block strong { display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: 100%; margin-top: 8px; color: #070D3F; font-size: 24px; line-height: 32px; font-weight: 400; }
.bill-date-icon { position: relative; display: inline-block; flex: 0 0 auto; width: 26px; height: 26px; border: 2px solid #070D3F; border-radius: 4px; box-sizing: border-box; }
.bill-date-icon::before { position: absolute; top: 7px; left: 3px; right: 3px; height: 2px; background: #070D3F; content: ''; }
.bill-date-icon::after { position: absolute; top: -5px; left: 5px; width: 12px; height: 8px; border-left: 2px solid #070D3F; border-right: 2px solid #070D3F; content: ''; }
.bill-time-block .bill-date-value { display: inline-block; color: #000000; line-height: 26px; transform: translateY(2px); }
.bill-section-title { display: flex; align-items: center; height: 40px; border-bottom: 2px solid #E5EAF3; color: #070D3F; font-size: 28px; line-height: 40px; font-weight: 500; }
.bill-section-icon { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 40px; height: 40px; margin-right: 16px; border-radius: 50%; color: #22C55E; background: #EAFBF2; font-size: 24px; }
.bill-summary-card { margin-top: 8px; padding: 20px 16px 16px; border-radius: 16px; background: #F6F9FF; }
.bill-summary-head { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: start; column-gap: 16px; padding-top: 0; }
.bill-summary-head > div:first-child { min-width: 0; }
.bill-total { display: block; margin-top: 2px; color: #0B45FF; font-size: 34px; line-height: 38px; font-weight: 500; }
.bill-due { flex-shrink: 0; text-align: right; }
.bill-due strong { display: block; margin-top: 2px; color: #070D3F; font-size: 24px; line-height: 30px; font-weight: 400; }
.bill-breakdown { margin-top: 16px; border-top: 2px solid #E5EAF3; }
.bill-breakdown-row { min-height: 64px; padding: 12px 0; border-bottom: 2px solid #E5EAF3; }
.bill-breakdown-row > span { min-width: 0; }
.bill-breakdown-row strong,
.bill-total-row strong,
.bill-installment__row strong,
.bill-payoff-summary strong { flex-shrink: 0; color: #070D3F; font-size: 24px; line-height: 32px; font-weight: 400; }
.bill-period-label,
.bill-late-fee-label { display: flex; align-items: center; flex: 1; }
.bill-period-chip,
.bill-late-fee-icon { display: inline-flex; flex: 0 0 auto; align-items: center; justify-content: center; width: 48px; height: 48px; margin-right: 16px; border-radius: 50%; color: #FFFFFF; font-style: normal; font-size: 22px; line-height: 48px; }
.bill-period-chip { background: #0B45FF; }
.bill-late-fee-icon { background: #F12B1C; }
.bill-period-copy { display: flex; min-width: 0; flex-direction: column; }
.bill-period-copy em { color: #070D3F; font-style: normal; font-size: 24px; line-height: 32px; font-weight: 400; }
.bill-period-copy small { margin-top: 2px; color: #6B7280; font-size: 22px; line-height: 28px; }
.bill-breakdown-row--danger .bill-period-copy em,
.bill-breakdown-row--danger strong { color: #F12B1C; }
.bill-late-fee-help { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; margin-left: 8px; padding: 0; border: 2px solid #6B7280; border-radius: 50%; background: transparent; color: #6B7280; font-size: 18px; line-height: 24px; font-weight: 500; vertical-align: 1px; }
.bill-total-row { padding-top: 16px; }
.bill-total-row span { position: relative; padding-left: 30px; color: #6B7280; font-size: 24px; line-height: 32px; font-weight: 400; }
.bill-total-row span::before { position: absolute; top: 50%; left: 0; width: 12px; height: 12px; margin-top: -6px; border-radius: 50%; background: #0B45FF; content: ''; }
.bill-total-row strong { color: #0B45FF; font-size: 26px; line-height: 36px; }
.bill-primary-action { box-sizing: border-box; width: 100%; height: 92px; margin-top: 16px; border-radius: 16px; color: #FFFFFF; background: #0B45FF; font-size: 28px; line-height: 92px; font-weight: 500; text-align: center; }
.bill-primary-action.is-disabled,
.bill-action-card.is-disabled { opacity: 0.5; pointer-events: none; }
.bill-action-card { display: flex; align-items: center; min-height: 92px; text-align: left; cursor: pointer; }
.bill-action-icon { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 56px; height: 56px; margin-right: 16px; border-radius: 16px; color: #0B45FF; background: #EAF3FF; font-size: 30px; }
.bill-action-copy { display: flex; flex: 1; min-width: 0; flex-direction: column; }
.bill-action-copy strong { color: #070D3F; font-size: 26px; line-height: 36px; font-weight: 500; }
.bill-action-copy small { margin-top: 4px; color: #6B7280; font-size: 24px; line-height: 32px; }
.bill-action-card > .van-icon { color: #6B7280; font-size: 32px; }
.bill-secure-tip { display: flex; align-items: center; justify-content: center; padding: 8px 16px 16px; color: #6B7280; font-size: 22px; line-height: 32px; font-weight: 400; text-align: center; }
.bill-secure-tip .van-icon { margin-right: 8px; font-size: 28px; }

.android-repay-plan-sheet { padding: 16px 28px calc(16px + env(safe-area-inset-bottom)); color: #172554; }
.android-repay-plan-sheet__header { display: flex; align-items: center; justify-content: space-between; min-height: 88px; border-bottom: 2px solid #E5EAF3; }
.android-repay-plan-sheet__title { color: #070D3F; font-size: 32px; line-height: 44px; font-weight: 500; }
.android-repay-plan-sheet__header > .van-icon { display: flex; align-items: center; justify-content: center; width: 56px; height: 56px; border-radius: 50%; background: #F5F7FC; color: #64748B; font-size: 40px; }
.android-repay-plan-sheet__return { margin-top: 16px; color: #0B45FF; font-size: 26px; line-height: 36px; text-align: right; }
.android-repay-plan-sheet__body { max-height: 68vh; overflow-y: auto; padding-top: 24px; }
.android-repay-plan-sheet__notice { display: flex; align-items: center; gap: 16px; box-sizing: border-box; width: 100%; padding: 20px; border-radius: 16px; background: #EAF3FF; }
.android-repay-plan-sheet__notice-icon { display: flex; align-items: center; justify-content: center; flex: 0 0 48px; width: 48px; height: 48px; border-radius: 8px; background: #FFFFFF; color: #0B45FF; }
.android-repay-plan-sheet__notice-icon .van-icon,
.android-repay-plan-sheet__notice-icon .android-order-icon { width: 30px; height: 30px; font-size: 30px; }
.android-repay-plan-sheet__notice > div { display: flex; min-width: 0; flex: 1; flex-direction: column; justify-content: center; }
.android-repay-plan-sheet__notice > div > strong,
.android-repay-plan-sheet__notice > div > span { display: block; }
.android-repay-plan-sheet__notice > div > strong { color: #070D3F; font-size: 26px; line-height: 36px; font-weight: 500; }
.android-repay-plan-sheet__notice > div > span { margin-top: 4px; color: #64748B; font-size: 24px; line-height: 32px; }
.android-repay-plan-sheet__state { padding: 56px 16px; color: #64748B; font-size: 26px; line-height: 36px; text-align: center; }
.android-repay-plan-sheet__items { display: grid; gap: 16px; margin-top: 24px; }
.android-repay-plan-item { box-sizing: border-box; width: 100%; padding: 20px; border: 2px solid #E5EAF3; border-radius: 16px; }
.android-repay-plan-item__top,
.android-repay-plan-item__row { display: flex; justify-content: space-between; gap: 16px; }
.android-repay-plan-item__top { align-items: center; font-size: 26px; line-height: 36px; }
.android-repay-plan-item__period { padding: 6px 14px; border-radius: 14px; color: #0B45FF; background: #EAF3FF; font-weight: 400; }
.android-repay-plan-item__row { margin-top: 12px; color: #64748B; font-size: 24px; line-height: 32px; }
.android-repay-plan-item__row strong { color: #0B45FF; font-weight: 400; }
.android-repay-plan-item__date { color: #0B1440 !important; font-weight: 400 !important; }
.android-repay-plan-item__amount { color: #0B45FF !important; font-weight: 400 !important; }
.android-repay-plan-item__status { display: inline-flex; align-items: center; gap: 8px; padding: 0; font-size: 22px; line-height: 30px; }
.android-repay-plan-item__status .van-icon,
.android-repay-plan-item__status .android-order-icon { width: 28px; height: 28px; flex: 0 0 28px; font-size: 28px; }
.android-repay-plan-item__status--paid { color: #15803D; }
.android-repay-plan-item__status--overdue { color: #DC2626; }
.android-repay-plan-item__status--unpaid { color: #F97316; }
.android-repay-plan-item__status--upcoming { color: #64748B; }
.android-repay-plan-sheet__notice--secure { margin-top: 24px; }

.android-payoff-sheet { box-sizing: border-box; padding: 32px 32px calc(16px + env(safe-area-inset-bottom)); color: #070D3F; background: #FFFFFF; }
.android-payoff-sheet__header { display: flex; align-items: flex-start; justify-content: space-between; min-height: 88px; border-bottom: 2px solid #E5EAF3; }
.android-payoff-sheet__header h3 { margin: 0; color: #070D3F; font-size: 32px; line-height: 44px; font-weight: 400; }
.android-payoff-sheet__header p { margin: 8px 0 0; color: #64748B; font-size: 26px; line-height: 36px; font-weight: 400; }
.android-payoff-sheet__header > .van-icon { display: flex; align-items: center; justify-content: center; flex: 0 0 56px; width: 56px; height: 56px; margin-left: 16px; border-radius: 50%; color: #64748B; background: #F5F7FC; font-size: 36px; }
.android-payoff-sheet__body { max-height: 62vh; overflow-y: auto; padding-top: 24px; }
.android-payoff-sheet__notice { display: flex; align-items: center; box-sizing: border-box; width: 100%; padding: 20px; border-radius: 16px; color: #070D3F; background: #EAF3FF; font-size: 24px; line-height: 34px; font-weight: 400; }
.android-payoff-sheet__notice-icon { display: flex; align-items: center; justify-content: center; flex: 0 0 48px; width: 48px; height: 48px; margin-right: 16px; color: #0B45FF; font-size: 30px; }
.android-payoff-sheet__section-title { margin-top: 32px; margin-bottom: 20px; color: #070D3F; font-size: 26px; line-height: 36px; font-weight: 400; }
.android-payoff-sheet__items { display: grid; gap: 16px; }
.android-payoff-summary { box-sizing: border-box; width: 100%; margin-top: 20px; padding: 24px; border: 2px solid #E5EAF3; border-radius: 16px; background: #FFFFFF; }
.android-payoff-summary > div { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; color: #64748B; font-size: 24px; line-height: 32px; }
.android-payoff-summary > div + div { margin-top: 16px; }
.android-payoff-summary strong { flex-shrink: 0; color: #070D3F; font-weight: 400; text-align: right; }
.android-payoff-summary__late-fee { align-items: center !important; color: #EF4444 !important; }
.android-payoff-summary__late-fee strong { color: #EF4444; }
.android-payoff-summary__late-fee > span { display: inline-flex; align-items: center; min-width: 0; color: #EF4444; line-height: 32px; }
.android-payoff-summary__late-fee-help { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 26px; width: 26px; height: 26px; margin-left: 8px; padding: 0; border: 2px solid #64748B; border-radius: 50%; color: #64748B !important; background: #FFFFFF; font-size: 18px; line-height: 22px; font-weight: 400; vertical-align: middle; }
.android-payoff-summary__total { padding-top: 16px; border-top: 2px solid #E5EAF3; color: #070D3F !important; }
.android-payoff-summary__total strong { color: #0B45FF; font-size: 28px; line-height: 38px; }
.android-payoff-sheet__action { box-sizing: border-box; width: 100%; height: 92px; margin-top: 24px; border-radius: 16px; color: #FFFFFF; background: #0B45FF; font-size: 30px; line-height: 92px; font-weight: 400; text-align: center; }
.android-payoff-sheet__action.is-disabled { opacity: 0.5; pointer-events: none; }
.android-payoff-sheet__secure { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 16px 8px 0; color: #6B7280; font-size: 22px; line-height: 32px; font-weight: 400; text-align: center; }
.android-payoff-sheet__secure .van-icon { font-size: 28px; }

::v-deep .late-fee-popup-wrapper {
  overflow: visible;
  border-radius: 28px;
  background: transparent;
}

.late-fee-popup {
  position: relative;
  box-sizing: border-box;
  width: 650px;
  padding: 72px 36px 36px;
  border-radius: 28px;
  background: #FFFFFF;
  text-align: center;
}

.late-fee-popup__close {
  position: absolute;
  top: 34px;
  right: 34px;
  border: 0;
  background: transparent;
  color: #9CA3AF;
  font-size: 44px;
  line-height: 44px;
}

.late-fee-popup__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 92px;
  height: 92px;
  border: 12px solid #EEF4FF;
  border-radius: 24px;
  background: #0B45FF;
  color: #FFFFFF;
  font-size: 42px;
  line-height: 42px;
  font-weight: 500;
}

.late-fee-popup__title {
  margin-top: 32px;
  color: #070D3F;
  font-size: 34px;
  line-height: 44px;
  font-weight: 500;
}

.late-fee-popup__desc {
  margin-top: 20px;
  color: #6B7280;
  font-size: 26px;
  line-height: 36px;
}

.late-fee-popup__confirm {
  width: 100%;
  height: 92px;
  margin-top: 36px;
  border: 0;
  border-radius: 18px;
  background: #0B45FF;
  color: #FFFFFF;
  font-size: 28px;
  line-height: 92px;
  font-weight: 400;
}

.detail-pull-refresh::v-deep .van-pull-refresh__text {
  font-size: 24px;
  line-height: 34px;
  font-weight: 400;
  color: #64748B;
}
</style>
